import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ticketBusinessAppsQuery, type TicketEscalationApp, type TicketEscalationRecord } from '../api/businessApps'
import { ticketVendorCasesQuery, type VendorCase } from '../api/vendorCases'
import { toast } from '../ui/notify'
import { TicketVendorCases } from './TicketVendorCases'
import { VendorEscalateModal } from './VendorEscalateModal'
import './VendorEscalation.css'

/**
 * Banda aplicatiilor de business pe fisa tichetului.
 *
 * Fiecare escaladare trimisa la furnizorul unei aplicatii de business se arata
 * ca un rand: "Escalated to X - response due <data>".
 *
 * Banda NU mai are butonul "Escalate to <furnizor> support": escaladarea noua se
 * face din formularul Waiting for Third Party (furnizorul aplicatiei vine
 * preselectat, emailul din sablon, si se deschide si cazul la furnizor).
 *
 * Fara cazuri si fara randuri, inainte de migrare sau cu capatul cazut, banda nu
 * se deseneaza deloc: fisa ramane exact cum era.
 *
 * Fiecare escaladare e si un CAZ la furnizor (`TicketVendorCases`): starea,
 * numarul lor, termenul primului raspuns, nivelul urmator ("Escalate to level
 * N", care deschide `VendorEscalateModal` pe acelasi caz). Escaladarile care
 * apartin unui caz nu se mai arata o data ca randuri separate.
 */
export function TicketVendorEscalation({ id }: { id: number }) {
  const { data } = useQuery({ ...ticketBusinessAppsQuery(id), retry: false })
  const { data: cases } = useQuery({ ...ticketVendorCasesQuery(id), retry: false })
  const [chosen, setChosen] = useState<{ app: TicketEscalationApp; next?: VendorCase } | null>(null)

  if (!data || !data.ready) return null
  const apps = data.apps
  const caseList = cases?.ready === true && Array.isArray(cases.cases) ? cases.cases : []
  const inCase = new Set(caseList.flatMap((c) => c.escalationIds ?? []))
  const records = data.escalations.filter((r) => !inCase.has(r.id))
  const hasCases = caseList.length > 0
  if (records.length === 0 && !hasCases) return null

  const escalateNext = (c: VendorCase) => {
    const app = apps.find((a) => a.id === c.appId)
    if (!app) {
      toast(`${c.appName} is no longer on this ticket's devices; escalate from a ticket that has it.`, 'Vendor case', 'error')
      return
    }
    setChosen({ app, next: c })
  }

  return (
    <div className="tk-vendor">
      {cases && hasCases && <TicketVendorCases ticketId={id} page={cases} onEscalateNext={escalateNext} />}

      {records.map((r) => (
        <EscalationLine key={r.id} record={r} />
      ))}

      {chosen && (
        <VendorEscalateModal
          key={`${chosen.app.id}-${chosen.next?.id ?? 0}`}
          ticketId={id}
          app={chosen.app}
          nextOf={chosen.next}
          waiting={cases?.ready === true && cases.canWaitThirdParty === true ? { providers: cases.externalProviders ?? [], reasons: cases.waitingReasons ?? [] } : undefined}
          open
          onClose={() => setChosen(null)}
        />
      )}
    </div>
  )
}

function EscalationLine({ record }: { record: TicketEscalationRecord }) {
  const who = record.contactName !== '' ? `${record.contactName} <${record.to}>` : record.to
  return (
    <div className={`tk-vendor-row tk-vendor-record${record.overdue ? ' tk-vendor-overdue' : ''}`}>
      <span className="tk-vendor-label">Escalated</span>
      <span>
        Escalated to <strong>{record.supportName}</strong> ({record.appName}
        {record.level > 0 ? `, level ${record.level}` : ''}) -{' '}
        {record.dueAt !== null ? (
          <>
            response due <strong>{record.dueAt}</strong>
            {record.overdue && <span className="label label-danger tk-vendor-late">overdue</span>}
          </>
        ) : (
          'no response time in contract'
        )}
        <span className="text-muted tk-vendor-who">
          {' '}
          · {who} · {record.createdAt}
          {!record.sentByEmail && ' · sent outside the portal'}
        </span>
      </span>
    </div>
  )
}
